<template>
    <v-dialog
        v-model="isShowOrderAddServiceModal"
        class="order-service-modal-wrapper"
        :class="{
            'sf-wrapper--light sf-service-wrapper--light': $conf.public.layout.searchForm.light,
        }"
    >
        <v-card class="overflow-visible">
            <v-card-title
                class="text-wrap d-flex align-center"
                style="background-color: rgba(0, 0, 0, 0.6); color: white"
            >
                <v-spacer />
                <span>
                    {{ `${$t('adding_service_to_order')} #${order.orderId}` }}
                </span>
                <v-spacer />
                <v-btn variant="text" icon="mdi-close" @click="isShowOrderAddServiceModal = false" />
            </v-card-title>
            <search-form-tabs
                v-if="initialized"
                ref="searchFormTabs"
                class="order-service-modal-wrapper__tabs"
                :services="order && order.services"
            />
            <v-sheet
                v-else
                class="full-width d-flex justify-center py-8 order-service-modal-wrapper__progress"
                color="rgba(0, 0, 0, 0.6)"
            >
                <v-progress-circular size="64" indeterminate />
            </v-sheet>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import SearchFormTabs from '~src/components/searchFormTabs.src'
    import {clone} from '@/utils/helpers'
    import getOrderTravelers from '@/adapters/orderTravelersAdapter'
    import {searchRequest as searchRequestTransfers} from '@/utils/transfers/transfers-blank-states'
    import {searchRequest as searchRequestActivities} from '~src/utils/activities/activities-blank-states'
    import {searchRequest as searchRequestFlights} from '~src/utils/flights/flights-blank-states.src'
    import {searchRequest as searchRequestPackages} from '@/utils/packages/packages-blank-states'
    import {searchRequest as searchRequestTours} from '~/utils/tours/tours-blank-states'
    import {searchRequest as searchRequestInsurance} from '~/utils/insurance/insurance-blank-states'
    import {searchRequest as searchRequestCruises} from '@/utils/cruises/cruises-blank-states'
    import {searchRequest as searchRequestCars} from '@/utils/cars/cars-rent-blank-state'
    import {searchRequest as searchRequestExtraServices} from '@/utils/extraServices/extra-services-blank-states'
    import {searchRequest as searchRequestHotels} from '~src/utils/hotels/hotels-blank-states.src'
    import {OrderSelectionMixin} from '@/components/account/mixins/OrderSelectionMixin.vue'

    @Component({
        components: {
            SearchFormTabs,
        },
    })
    export class OrderAddServiceModal extends mixins(OrderSelectionMixin) {
        @VModel() isShowOrderAddServiceModal
        @Prop({required: true}) order
        @Prop({default: true, type: Boolean}) prefillTransferData
        @Prop({default: () => [], type: Array}) destinationLocations

        persistentTourists = []
        hotelsTourists = []
        initialized = false
        destination = false

        @Watch('isShowOrderAddServiceModal', {immediate: true})
        async onDialogShow() {
            this.initialized = false
            this.reset()

            if (this.isShowOrderAddServiceModal) {
                if (this.destination) {
                    persistentStore.SET_SELECTED_DESTINATION(this.destination)
                }
                await this.setSelectedOrderAndCompany()
                this.persistentTourists = clone(persistentStore.tourists)
                this.hotelsTourists = clone(hotelsStore.tourists)

                const tourists = getOrderTravelers(this.order).map(
                    ({
                        prefix,
                        name: [{firstName, lastName}],
                        citizenshipId,
                        isChild,
                        phone,
                        email,
                        dateOfBirth: birthdate,
                        passports: [passport],
                        age,
                        personId,
                    }) => {
                        return {
                            prefix: prefix || null,
                            firstName: firstName || null,
                            lastName: lastName || null,
                            citizenshipId: citizenshipId || null,
                            type: isChild ? 'child' : 'adult',
                            ...(!isChild && {phone: phone || null}),
                            ...(!isChild && {email: email || null}),
                            birthdate: birthdate || null,
                            ...(isChild && {
                                age:
                                    age ||
                                    this.$dateFns.differenceInYears(new Date(), this.$dateFns.parseISO(birthdate)) ||
                                    2,
                            }),
                            passport: {
                                type: passport?.type || null,
                                number: passport?.number || null,
                                issueDate: passport?.issueDate || null,
                                expiryDate: passport?.expiryDate || null,
                            },
                            personId,
                        }
                    }
                )

                persistentStore.SET_TOURISTS(tourists)
                hotelsStore.SET_TOURISTS([tourists])

                const accommodationService = this.order.services.find(
                    service => service.serviceType === 'ACCOMMODATION'
                )
                const transferService = this.order.services.find(service => service.serviceType === 'TRANSFER')
                const flightService = this.order.services.find(
                    service => service.serviceType === 'OWNCHARTER' || service.serviceType === 'FLIGHT'
                )
                let adults = 0,
                    childrenAges = []

                tourists.forEach(tourist => {
                    if (tourist.type === 'adult') {
                        adults += 1
                    } else {
                        childrenAges.push(tourist.age)
                    }
                })

                if (accommodationService && transferService) {
                    const {hotelCode, hotelName} = accommodationService.serviceDetails[0]
                    const {cityId} = persistentStore.selectedDestination
                        ? persistentStore.selectedDestination
                        : accommodationService.serviceDetails[0]
                    const {countryId} = persistentStore.selectedDestination
                        ? this.destinationLocations.find(
                              destinationLocation =>
                                  destinationLocation.id === persistentStore.selectedDestination.cityId
                          )
                        : accommodationService.serviceDetails[0]
                    const {dateFrom, dateTo} = persistentStore.selectedDestination
                        ? persistentStore.selectedDestination
                        : {}
                    if (this.prefillTransferData) {
                        transfersRuntimeStore.SET_DEPARTURE_POINT({})
                        transfersRuntimeStore.SET_ARRIVAL_POINT({})

                        await transfersRuntimeStore.loadDeparturePoint({
                            departureHotelName: hotelName,
                            departureCityId: cityId,
                        })
                        transfersStore.SET_SEARCH_REQUEST({
                            ...searchRequestTransfers(),
                            departureCityId: cityId,
                            ...(adults && {adults}),
                            ...(childrenAges.length && {childrenAges}),
                            dateTime: accommodationService.endDateTime,
                        })
                    }
                    this.fillActivitiesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillFlightForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillPackagesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillToursForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillInsuranceForm({adults, childrenAges, countryId, dateFrom, dateTo})
                    this.fillCruisesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillCarsForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillExtraServicesForm({adults, childrenAges, cityId, countryId, dateFrom, dateTo})
                    this.fillHotelForm({
                        adults,
                        childrenAges,
                        cityId,
                        countryId,
                        hotelCode,
                        hotelName,
                        dateFrom,
                        dateTo,
                    })
                } else if (accommodationService) {
                    const {hotelCode, hotelName} = accommodationService.serviceDetails[0]
                    const {cityId} = persistentStore.selectedDestination
                        ? persistentStore.selectedDestination
                        : accommodationService.serviceDetails[0]
                    const {countryId} = persistentStore.selectedDestination
                        ? this.destinationLocations.find(
                              destinationLocation =>
                                  destinationLocation.id === persistentStore.selectedDestination.cityId
                          )
                        : accommodationService.serviceDetails[0]
                    const {dateFrom, dateTo} = persistentStore.selectedDestination
                        ? persistentStore.selectedDestination
                        : {}
                    if (this.prefillTransferData) {
                        transfersRuntimeStore.SET_DEPARTURE_POINT({})
                        transfersRuntimeStore.SET_ARRIVAL_POINT({})

                        await transfersRuntimeStore.loadArrivalPoint({
                            arrivalHotelName: hotelName,
                            arrivalCityId: cityId,
                        })
                        const searchRequest = {
                            ...searchRequestTransfers(),
                            arrivalCityId: cityId,
                            ...(adults && {adults}),
                            ...(childrenAges.length && {childrenAges}),
                            dateTime: accommodationService.startDateTime,
                        }
                        if (flightService) {
                            const {arrivalAirportCode} =
                                flightService.serviceDetails[0].itineraries[0].flightSegments[0]
                            const departureFlightNumber =
                                flightService.serviceDetails[0].itineraries[0].flightSegments[0].flightNumber
                            searchRequest.departurePlace = arrivalAirportCode
                            searchRequest.flightNumber = departureFlightNumber
                            searchRequest.departureCityId = cityId
                            searchRequest.departureCountryId = countryId

                            await transfersRuntimeStore.loadDeparturePoint({
                                departurePlace: arrivalAirportCode,
                                departureCityId: cityId,
                                departureCountryId: countryId,
                                flightNumber: departureFlightNumber,
                            })
                        }
                        transfersStore.SET_SEARCH_REQUEST(searchRequest)
                    }
                    this.fillActivitiesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillFlightForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillPackagesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillToursForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillInsuranceForm({adults, childrenAges, countryId, dateFrom, dateTo})
                    this.fillCruisesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillCarsForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillExtraServicesForm({adults, childrenAges, cityId, countryId, dateFrom, dateTo})
                    this.fillHotelForm({
                        adults,
                        childrenAges,
                        cityId,
                        countryId,
                        hotelCode,
                        hotelName,
                        dateFrom,
                        dateTo,
                    })
                } else if (persistentStore.selectedDestination) {
                    const {cityId, dateFrom, dateTo} = persistentStore.selectedDestination
                    const {countryId} = this.destinationLocations.find(
                        destinationLocation => destinationLocation.id === persistentStore.selectedDestination.cityId
                    )
                    this.fillActivitiesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillFlightForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillPackagesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillToursForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillInsuranceForm({adults, childrenAges, countryId, dateFrom, dateTo})
                    this.fillCruisesForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillCarsForm({adults, childrenAges, cityId, dateFrom, dateTo})
                    this.fillExtraServicesForm({adults, childrenAges, cityId, countryId, dateFrom, dateTo})
                    this.fillHotelForm({
                        adults,
                        childrenAges,
                        cityId,
                        countryId,
                        dateFrom,
                        dateTo,
                    })
                }
            }

            this.initialized = true
        }

        showWithDestination(destination) {
            this.destination = destination
            this.isShowOrderAddServiceModal = true
        }

        reset() {
            this.resetSelectedOrderAndCompany()
            persistentStore.SET_TOURISTS(this.persistentTourists)
            hotelsStore.SET_TOURISTS(this.hotelsTourists)
            if (this.prefillTransferData) {
                transfersRuntimeStore.SET_DEPARTURE_POINT({})
                transfersRuntimeStore.SET_ARRIVAL_POINT({})
                transfersStore.SET_SEARCH_REQUEST(searchRequestTransfers())
            }
            activitiesRuntimeStore.SET_CITY({})
            activitiesStore.SET_SEARCH_REQUEST({...activitiesStore.searchRequest, cityId: null})
            persistentStore.SET_SELECTED_PRIVATE_CLIENT(null)
        }

        fillActivitiesForm({adults, childrenAges, cityId, dateFrom, dateTo}) {
            activitiesRuntimeStore.loadCity(cityId)
            activitiesStore.SET_SEARCH_REQUEST({
                ...searchRequestActivities(),
                cityId: cityId,
                ...(adults && {adults}),
                ...(childrenAges.length && {childrenAges}),
                ...(dateFrom && {startDate: dateFrom}),
                ...(dateTo && {endDate: dateTo}),
            })
        }

        async fillFlightForm({adults, childrenAges, cityId}) {
            flightsRuntimeStore.SET_ROUTES([])
            await flightsRuntimeStore.SET_ARRIVAL_LOCATION(cityId)
            flightsStore.SET_SEARCH_REQUEST({
                ...searchRequestFlights(),
                ...(adults && {adults}),
                ...(childrenAges.length && {childrenAges}),
            })
        }

        fillPackagesForm({adults, childrenAges, cityId, dateFrom, dateTo}) {
            packagesRuntimeStore.loadArrivalPoint(cityId)
            packagesStore.SET_SEARCH_REQUEST({
                ...searchRequestPackages(),
                ...(adults && {adults}),
                ...(childrenAges.length && {childrenAges}),
                arrivalCityId: cityId,
                ...(dateFrom && {startDate: dateFrom}),
                ...(dateTo && {endDate: dateTo}),
            })
        }

        fillToursForm({adults, childrenAges, cityId, dateFrom, dateTo}) {
            toursRuntimeStore.loadArrivalCity(cityId)
            toursStore.SET_SEARCH_REQUEST({
                ...searchRequestTours(),
                ...(adults && {adults}),
                ...(childrenAges.length && {childrenAges}),
                arrivalCityId: cityId,
                ...(dateFrom && {startDateFrom: dateFrom}),
                ...(dateTo && {startDateTo: dateTo}),
            })
        }

        fillInsuranceForm({adults, childrenAges, countryId, dateFrom, dateTo}) {
            insuranceStore.SET_SEARCH_REQUEST({
                ...searchRequestInsurance(),
                locationId: countryId,
                ...(adults && {adults}),
                ...(childrenAges.length && {childrenAges}),
                ...(dateFrom && {startDate: dateFrom}),
                ...(dateTo && {endDate: dateTo}),
            })
        }

        fillCruisesForm({adults, childrenAges, cityId, dateFrom, dateTo}) {
            cruisesStore.SET_SEARCH_REQUEST({
                ...searchRequestCruises(),
                ...(adults && {adults}),
                ...(childrenAges.length && {childrenAges}),
                departureCityId: cityId,
                destinationCityId: cityId,
                ...(dateFrom && {startDate: dateFrom}),
                ...(dateTo && {endDate: dateTo}),
            })
        }

        fillCarsForm({cityId, dateFrom, dateTo}) {
            carsRentStore.SET_SEARCH_REQUEST({
                ...searchRequestCars(),
                pickUpCityId: cityId,
                dropOffCityId: cityId,
                ...(dateFrom && {pickUpDateTime: dateFrom + '10:00'}),
                ...(dateTo && {dropOffDateTime: dateTo + '10:00'}),
            })
        }

        fillExtraServicesForm({cityId, countryId, dateFrom}) {
            extraServicesStore.SET_SEARCH_REQUEST({
                ...searchRequestExtraServices(),
                countryId,
                cityId,
                ...(dateFrom && {startDate: dateFrom}),
            })
        }

        fillHotelForm({cityId, countryId, dateFrom, dateTo}) {
            hotelsStore.SET_SEARCH_REQUEST({
                ...searchRequestHotels(),
                countryId,
                cityId,
                ...(dateFrom && {startDate: dateFrom}),
                ...(dateTo && {endDate: dateTo}),
            })
        }
    }

    export default OrderAddServiceModal
</script>

<style lang="scss" scoped>
    .v-card-title {
        background-color: rgba(0, 0, 0, 0.6);
        color: white;
        border-top-left-radius: 4px !important;
        border-top-right-radius: 4px !important;
    }

    :deep(div .v-tabs) {
        border-top-left-radius: 0 !important;
        border-top-right-radius: 0 !important;
    }
</style>
